import {Box, Text} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import Image from 'next/image';
import {useRouter} from 'next/router';
import React from 'react';
import {GSSKT_HOST} from '../../../../../config/constants/hosts';
import type {Recruitment} from '../../../../../core/repositories/recruitingInfoRepository';
import {cn} from '../../../../../lib/classname';
import {CommonLink} from '../../../../../shared/components/CommonLink';
import {
  ENTRY_BUTTON_ID,
  ENTRY_BUTTON_TEXT_SIZE,
  GradientEntryButton,
} from '../../../../../shared/components/EntryButton/GradientEntryButton';
import {appendQueryParam} from '../../../../../shared/services/appendQueryParam';
import {buildScheduleGtmContent} from '../../../../../shared/services/buildScheduleGtmContent';
import {ScheduleButtonNewDesign} from '../../../../recruitment/components/ScheduleButton';
import {isEntryRegisterModalExperimentEnabled} from '../../../usecase/entryRegisterModalExperiment';
import {useCalendarPromptModal} from '../../../usecase/useCalendarPromptModal';
import {CompanyLogoMenu} from '../CompanyLogoMenu';
import {EntryRegisterModal} from '../EntryRegisterModal';
import {Deadline} from './Deadline';
import {FollowCompletionModal} from './FollowCompletionModal';
import {Schedules} from './Schedules';
import {Tags} from './Tags';

type Props = {
  recruitment: Recruitment;
  className?: string;
  hasTargetYear?: boolean;
  /** 未ログイン時のエントリー押下でその場に登録モーダルを出す（募集一覧のみ） */
  withEntryRegisterModal?: boolean;
};

export const CardForPc: React.FC<Props> = ({
  recruitment,
  className,
  hasTargetYear = true,
  withEntryRegisterModal = false,
}) => {
  const {asPath} = useRouter();
  const [
    registerModalOpened,
    {open: openRegisterModal, close: closeRegisterModal},
  ] = useDisclosure(false);
  // VWO のバリエーション側だけモーダルを出す。VWO は非同期に読み込まれるのでクリック時に判定する
  const handleUnauthenticatedEntryClick = () => {
    if (!isEntryRegisterModalExperimentEnabled()) {
      return false;
    }
    openRegisterModal();
    return true;
  };
  const {
    modalVisible,
    suppressChecked,
    setSuppressChecked,
    handleCalendarClose,
    handleFollowSuccess,
  } = useCalendarPromptModal();

  const onFollowSuccess = () => {
    const firstSchedule = recruitment.schedules[0];
    if (!firstSchedule) {
      return;
    }
    handleFollowSuccess(firstSchedule.isCalendarRegistered);
  };

  const recruitingInfoPage = `/recruiting_info/${recruitment.category === 'archive' ? 'archives' : 'view'}/${recruitment.detailID}`;
  const openCompanyPage = (id: number) => {
    if (id === 0) {
      return;
    }

    window.open(`/company/${id}`, '_blank', 'noopener,noreferrer');
  };

  const firstCompany = recruitment.companies[0];
  const hasLogo = firstCompany?.logoURLs?.['140x105'];
  const canShowEntryButton =
    recruitment.entryURL &&
    ['medium', 'large'].includes(firstCompany?.pageType ?? '');

  const entryButtonUrl = recruitment.entryURL
    ? appendQueryParam(recruitment.entryURL, 'source_url', recruitingInfoPage)
    : '';

  return (
    <>
      <Box className={cn('bg-white rounded-[16px] shadow-card', className)}>
        <Box className="flex p-[16px]">
          <Box className="w-full">
            <Box className="w-full flex">
              <Box className="ml-96 flex flex-col justify-center bottom-[6px] w-auto grow">
                <Box className="flex items-center">
                  <Box className="flex flex-grow">
                    {recruitment.category === 'client' && hasLogo && (
                      <CompanyLogoMenu
                        companyId={firstCompany?.id ?? 0}
                        companyName={firstCompany?.name ?? ''}
                        isFollowed={firstCompany?.isFollowed ?? false}
                        followerCount={firstCompany?.followerCount ?? 0}
                        onFollowSuccess={onFollowSuccess}
                      >
                        <Image
                          className="mt-[6px] flex h-[60px] w-[94px] items-center bg-white object-contain cursor-pointer hover:opacity-50"
                          src={firstCompany.logoURLs['140x105']}
                          alt={recruitment.title}
                          width={94}
                          height={60}
                        />
                      </CompanyLogoMenu>
                    )}
                    <Box className="relative flex flex-col justify-between">
                      <CommonLink
                        href={recruitingInfoPage}
                        data-gtm-click-recruitment-detail-button="true"
                      >
                        <Text className="break-all text-xl font-bold leading-28 cursor-pointer hover:opacity-50 pl-12 pr-8 line-clamp-2">
                          {recruitment.title}
                        </Text>
                      </CommonLink>
                      <Box
                        onClick={() => {
                          openCompanyPage(firstCompany?.id ?? 0);
                        }}
                      >
                        <Text className="text-md text-black w-full cursor-pointer hover:opacity-50 pl-12 pr-8 line-clamp-2">
                          {recruitment.companies.map((company, i) => (
                            <React.Fragment key={company.id}>
                              {i > 0 && ', '}
                              <span>{company.name}</span>
                            </React.Fragment>
                          ))}
                        </Text>
                      </Box>
                    </Box>
                  </Box>
                  <Box className="flex flex-col items-center gap-8">
                    {recruitment.schedules.length > 0 &&
                      recruitment.schedules[0] && (
                        <>
                          <Deadline schedule={recruitment.schedules[0]} />
                          <ScheduleButtonNewDesign
                            scheduleId={recruitment.schedules[0].id}
                            isRegisteredInitial={
                              recruitment.schedules[0].isCalendarRegistered
                            }
                            registeredCountInitial={
                              recruitment.schedules[0].calendarRegisteredCount
                            }
                            recruitmentData={{
                              companyId: recruitment.companies[0]?.id ?? 0,
                              companyName: recruitment.companies[0]?.name ?? '',
                              entryTitle: recruitment.title,
                              detailURL: `${GSSKT_HOST}${recruitment.detailPageURL}`,
                            }}
                            dataGtmClick="recruitment-schedule-add"
                            dataGtmContent={buildScheduleGtmContent({
                              scheduleId: recruitment.schedules[0].id,
                              location: 'recruiting-info-card-pc',
                            })}
                          />
                        </>
                      )}
                  </Box>
                </Box>
                <Box className="text-sm text-black-2 cursor-default py-12">
                  <Tags
                    tags={recruitment.tags}
                    targetYear={recruitment.targetYear}
                    type={recruitment.type}
                    groupedJobTypes={recruitment.groupedJobTypes}
                    hasTargetYear={hasTargetYear}
                  />
                </Box>
                {recruitment.schedules.length > 0 &&
                  recruitment.schedules[0] && (
                    <Schedules
                      schedules={[recruitment.schedules[0]]}
                      recruitmentData={{
                        companyId: firstCompany?.id ?? 0,
                        companyName: firstCompany?.name ?? '',
                        detailURL: `${GSSKT_HOST}${recruitment.detailPageURL}`,
                      }}
                    />
                  )}
                <Box className="mt-[5px] flex justify-between items-start w-full pt-12">
                  <Box>
                    {recruitment.schedules.length > 1 && (
                      <CommonLink href={recruitingInfoPage}>
                        <Box className="text-md text-black-1 whitespace-nowrap cursor-pointer hover:opacity-50 pl-12">
                          ほか{recruitment.schedules.length - 1}日程
                        </Box>
                      </CommonLink>
                    )}
                  </Box>
                  <Box className="flex gap-8">
                    <CommonLink
                      href={recruitingInfoPage}
                      data-gtm-click-recruitment-detail-button="true"
                    >
                      <Box className="px-16 py-8 bg-line text-black text-md font-bold rounded-[8px] shadow-bottom hover:opacity-80">
                        募集詳細
                      </Box>
                    </CommonLink>
                    {canShowEntryButton && (
                      <GradientEntryButton
                        entryUrl={entryButtonUrl}
                        id={ENTRY_BUTTON_ID.RECRUITING_INFO}
                        size={ENTRY_BUTTON_TEXT_SIZE.BASE}
                        className="px-16 h-[40px]"
                        withShadow
                        companyId={firstCompany?.id}
                        companyName={firstCompany?.name}
                        entryTitle={recruitment.title}
                        deadline={recruitment.schedules[0]?.entryEnd}
                        onUnauthenticatedClick={
                          withEntryRegisterModal
                            ? handleUnauthenticatedEntryClick
                            : undefined
                        }
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      {withEntryRegisterModal && canShowEntryButton && (
        <EntryRegisterModal
          opened={registerModalOpened}
          onClose={closeRegisterModal}
          recruitment={recruitment}
          entryUrl={entryButtonUrl}
          recruitingInfoPage={recruitingInfoPage}
        />
      )}
      {recruitment.schedules[0] && (
        <FollowCompletionModal
          opened={modalVisible}
          onClose={handleCalendarClose}
          recruitmentId={recruitment.id}
          recruitmentTitle={recruitment.title}
          scheduleId={recruitment.schedules[0].id}
          isCalendarRegistered={recruitment.schedules[0].isCalendarRegistered}
          calendarRegisteredCount={
            recruitment.schedules[0].calendarRegisteredCount
          }
          companyId={firstCompany?.id ?? 0}
          companyName={firstCompany?.name ?? ''}
          detailURL={`${GSSKT_HOST}${recruitment.detailPageURL}`}
          pagePath={asPath}
          suppressChecked={suppressChecked}
          onSuppressChange={setSuppressChecked}
        />
      )}
    </>
  );
};
